/**
 * Checkbox Component - Controller
 * ===============================
 */

import type { IScope, ISCEService, INgModelController, ITranscludeFunction } from 'angular';
import { SIZES, BASE_CLASSES, CHECK_ICON, UNCHECK_ICON, DEFAULTS, CheckboxSize } from './constants';

export interface CheckboxBindings {
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  size?: CheckboxSize;
  inputId?: string;
  onChange?: (args: { checked: boolean }) => void;
}

export class CheckboxController implements CheckboxBindings {
  static $inject = ['$scope', '$sce', '$transclude'];

  private $scope: IScope | null;
  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  ngModelCtrl?: INgModelController | null;

  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  size?: CheckboxSize;
  inputId?: string;
  onChange?: (args: { checked: boolean }) => void;

  checked = false;
  focused = false;
  trustedCheck: unknown = null;
  trustedUncheck: unknown = null;
  resolvedId = '';

  // Slot nombrado (label): detectado con isSlotFilled, que SÍ funciona
  // para multi-slot transclusion (a diferencia del slot default booleano).
  hasLabelSlot = false;

  constructor($scope: IScope, $sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$scope = $scope;
    this.$sce = $sce;
    this.$transclude = $transclude;
  }

  $onInit(): void {
    this.size = this.size || DEFAULTS.size;
    const uid = this.$scope ? String(this.$scope.$id) : Math.random().toString(36).slice(2);
    this.resolvedId = this.inputId || ('ec-checkbox-' + uid);
    if (this.$sce) {
      this.trustedCheck = this.$sce.trustAsHtml(CHECK_ICON);
      this.trustedUncheck = this.$sce.trustAsHtml(UNCHECK_ICON);
    }
    if (this.$transclude) {
      this.hasLabelSlot = this.$transclude.isSlotFilled('labelSlot');
    }
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$render = () => {
        this.checked = !!(this.ngModelCtrl && this.ngModelCtrl.$viewValue);
      };
    }
  }

  $onDestroy(): void {
    this.$scope = null;
    this.$sce = null;
    this.$transclude = null;
    this.ngModelCtrl = null;
  }

  handleChange(): void {
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue(this.checked);
    }
    if (typeof this.onChange === 'function') {
      this.onChange({ checked: this.checked });
    }
  }

  getWrapperClasses(): string[] {
    const classes = [BASE_CLASSES.wrapper];
    if (this.disabled) classes.push(BASE_CLASSES.wrapperDisabled);
    return classes;
  }

  getBoxClasses(): string[] {
    const classes = [BASE_CLASSES.box, SIZES[this.size || DEFAULTS.size].box];
    classes.push(this.checked ? BASE_CLASSES.boxChecked : BASE_CLASSES.boxUnchecked);
    if (this.focused) classes.push(BASE_CLASSES.boxFocused);
    return classes;
  }

  getLabelClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].label, BASE_CLASSES.label];
  }
}

export default CheckboxController;
